/* Mod-Profil in den Karteneigenschaften: FGD-Dateien der Karte (Schlüssel xf_fgd) hinzufügen, ordnen, entfernen. */
import { useRef } from 'react';
import { COMPILER_FGDS, fgdNames, moveFgdName, removeFgdName } from '../../model/modprofile.ts';
import type { FgdStatus } from '../../model/modprofile.ts';
import { useT } from '../hooks.ts';

export interface FgdHost {
  /** Zustand der zuletzt eingesetzten FGDs */
  fgdStatus(): FgdStatus;
  /** Dateien einlesen, einsetzen, auf den Server laden (wenn angemeldet) und der Karte hinzufügen */
  addFgdFiles(files: File[]): Promise<void>;
  /** Mitgelieferte FGDs (Dateinamen) und eine davon der Karte hinzufügen */
  bundledFgds(): string[];
  /** Dateien, die nach dem Kartennamen automatisch gelten (leer, wenn die Karte eigene hat) */
  fgdAuto(): string[];
  addBundledFgd(name: string): Promise<void>;
}

export function FgdField(props: { host: FgdHost; value: string; onChange: (v: string) => void }) {
  const t = useT();
  const { host, value } = props, names = fgdNames(value), st = host.fgdStatus();
  const pick = useRef<HTMLInputElement>(null);
  const loaded = new Set(st.loaded.map((n) => n.toLowerCase()));
  return (
    <div style={{ display: 'grid', gap: 4, minWidth: 0 }} data-f="fgd">
      {names.length === 0 && (host.fgdAuto().length ? <span className="small">{t('pn.fgd_auto', { files: host.fgdAuto().join(', ') })}</span> : <div className="warnbox" role="status">{t('pn.fgd_none')}</div>)}
      {names.length > 0 && !names.some((n) => COMPILER_FGDS.includes(n.toLowerCase())) && host.bundledFgds().some((n) => n.toLowerCase() === COMPILER_FGDS[0]) && (
        <div className="row"><span className="small" style={{ flex: 1 }}>{t('pn.fgd_suggest')}</span><button className="btn sm" onClick={() => void host.addBundledFgd(COMPILER_FGDS[0])}>{t('pn.fgd_suggest_btn')}</button></div>
      )}
      {names.map((n, i) => (
        <div className="row" key={n}>
          <span className="small" style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }} title={n}>{n + (loaded.has(n.toLowerCase()) ? '' : ' · ' + t('pn.fgd_not_loaded'))}</span>
          <button className="btn sm" disabled={i === 0} title={t('pn.fgd_earlier')} aria-label={t('pn.fgd_earlier') + ' ' + n} onClick={() => props.onChange(moveFgdName(value, n, -1))}>↑</button>
          <button className="btn sm" disabled={i === names.length - 1} title={t('pn.fgd_later')} aria-label={t('pn.fgd_later') + ' ' + n} onClick={() => props.onChange(moveFgdName(value, n, 1))}>↓</button>
          <button className="btn sm x" title={t('pn.fgd_remove')} aria-label={t('pn.fgd_remove') + ' ' + n} onClick={() => props.onChange(removeFgdName(value, n))}>×</button>
        </div>
      ))}
      {host.bundledFgds().some((n) => !names.some((m) => m.toLowerCase() === n.toLowerCase())) && (
        <select className="sel" aria-label={t('pn.fgd_bundled')} title={t('pn.fgd_bundled_tip')} value="" onChange={(e) => { const n = e.target.value; if (n) void host.addBundledFgd(n); }}>
          <option value="">{t('pn.fgd_bundled')}</option>
          {host.bundledFgds().filter((n) => !names.some((m) => m.toLowerCase() === n.toLowerCase())).map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      )}
      <div className="row">
        <button className="btn sm" title={t('pn.fgd_add_tip')} onClick={() => pick.current?.click()}>{t('pn.fgd_add')}</button>
        <input ref={pick} type="file" accept=".fgd,.txt" multiple hidden aria-label={t('pn.fgd_add')}
          onChange={(e) => { const fs = Array.from(e.target.files || []); e.target.value = ''; if (fs.length) void host.addFgdFiles(fs); }} />
      </div>
      {st.included && st.included.length > 0 && <span className="small">{t('pn.fgd_included', { files: st.included.join(', ') })}</span>}
      {st.includeMissing && st.includeMissing.length > 0 && <span className="small">{t('pn.fgd_include_missing', { files: st.includeMissing.join(', ') })}</span>}
      {st.classes > 0 && <span className="small">{t('pn.fgd_classes', { count: st.classes })}</span>}
      {st.warnings.length > 0 && (
        <details className="gen">
          <summary>{t('pn.fgd_warnings', { count: st.warnings.length })}</summary>
          <div className="body"><ul className="small">{st.warnings.slice(0, 50).map((w, i) => <li key={i}>{w}</li>)}</ul></div>
        </details>
      )}
    </div>
  );
}

